/**
 * ServiceMapDataBuilder
 *
 * Transforms raw EA canister data into a ServiceMapData structure
 * for the concentric ring service map visualizer. Fetches all 15 EA
 * entity types in parallel, assigns ring positions (0-5), computes
 * galaxy-level compound nodes per division, and builds edges from
 * EA relationships + integration entities.
 *
 * Sprint 46 US-46.1: Ring-based data pipeline for Enterprise Service Map.
 *
 * Ring Strategy:
 *   0 (center)    — Organization root (1 node)
 *   1             — Divisions / Business Units (compound nodes, ~10)
 *   2             — Business Services + Capabilities (by domain, ~113)
 *   3             — Applications + Technical Services (by domain, ~66)
 *   4             — Infrastructure (Data Objects) + HR Roles (~101)
 *   5 (togglable) — Risks, Controls, Compliance (~277)
 *
 * Follows OOA/OOD Single Responsibility: this class is solely responsible
 * for the data transformation pipeline from canister → service map model.
 */

import type {
    EAOrganization,
    EAApplicationEntity,
    EATechnicalService,
    EABusinessServiceEntity,
    EADataObject,
    EAHRRole,
    EARelationship,
    EACapability,
    EAProcess,
    EAProcessConnector,
    EACompliance,
    EAIntegration,
    CanisterClientInterface,
} from '../canister-client';

import type { ConstellationNode, ConstellationEdge } from './constellation-data-builder';

// ---------------------------------------------------------------------------
// Ring Constants
// ---------------------------------------------------------------------------

export enum Ring {
    CENTER = 0,
    DIVISION = 1,
    SERVICES_CAPABILITIES = 2,
    APPLICATIONS = 3,
    INFRASTRUCTURE = 4,
    GOVERNANCE = 5,
}

// ---------------------------------------------------------------------------
// Exported Interfaces
// ---------------------------------------------------------------------------

export interface ServiceMapNode extends ConstellationNode {
    ring: Ring;
    ringGroup: string;
    isCompound?: boolean;
    childCount?: number;
}

export interface ServiceMapEdge extends ConstellationEdge {
    edgeCategory: EdgeCategory;
    style?: EdgeStyle;
}

export type EdgeCategory =
    | 'relationship'
    | 'integration'
    | 'risk-app'
    | 'control-risk'
    | 'compliance-app'
    | 'process-connector'
    | 'infrastructure';

export interface EdgeStyle {
    color: string;
    lineStyle: 'solid' | 'dashed' | 'dotted';
    opacity: number;
}

export interface ServiceMapData {
    nodes: ServiceMapNode[];
    edges: ServiceMapEdge[];
    statistics: ServiceMapStatistics;
    sessionId?: string;
}

export interface ServiceMapStatistics {
    totalEntities: number;
    byRing: Record<number, number>;
    byType: Record<string, number>;
    divisions: number;
    totalHeadcount: number;
    totalApplications: number;
    totalCapabilities: number;
    totalProcesses: number;
    totalIntegrations: number;
    totalCompliance: number;
    totalRisks: number;
    totalControls: number;
}

// ---------------------------------------------------------------------------
// Edge Color / Style Constants
// ---------------------------------------------------------------------------

const EDGE_STYLES: Record<EdgeCategory, EdgeStyle> = {
    'relationship':       { color: '#6B7280', lineStyle: 'solid',  opacity: 0.6 },
    'integration':        { color: '#3B82F6', lineStyle: 'dashed', opacity: 0.7 },
    'risk-app':           { color: '#F97316', lineStyle: 'solid',  opacity: 0.5 },
    'control-risk':       { color: '#22C55E', lineStyle: 'solid',  opacity: 0.5 },
    'compliance-app':     { color: '#14B8A6', lineStyle: 'dotted', opacity: 0.5 },
    'process-connector':  { color: '#8B5CF6', lineStyle: 'dashed', opacity: 0.5 },
};

// ---------------------------------------------------------------------------
// Relationship weight mapping (shared with constellation-data-builder)
// ---------------------------------------------------------------------------

const RELATIONSHIP_WEIGHT_MAP: Record<string, number> = {
    'integrates_with': 5,
    'supports': 4,
    'owns': 6,
    'uses': 3,
    'depends_on': 7,
    'mitigates': 5,
    'linked_to': 3,
};

const DEFAULT_RELATIONSHIP_WEIGHT = 2;

// ---------------------------------------------------------------------------
// Risk / Control types (same as constellation-data-builder)
// ---------------------------------------------------------------------------

interface RiskEntity {
    risk_id: string;
    name: string;
    description: string;
    category: string;
    severity: string;
    likelihood: string;
    owner: string;
    status: string;
    linked_controls: string[];
    linked_applications: string[];
    metadata: Record<string, string>;
}

interface ControlEntity {
    control_id: string;
    name: string;
    description: string;
    control_type: string;
    effectiveness: string;
    owner: string;
    linked_risks: string[];
    linked_applications: string[];
    metadata: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Domain-to-division alias mapping
// ---------------------------------------------------------------------------

type AliasEntry = { keywords: string[]; divisionHints: string[] };

const DOMAIN_ALIAS_TABLE: AliasEntry[] = [
    { keywords: ['banking', 'retail', 'branch'],          divisionHints: ['front office', 'retail'] },
    { keywords: ['cards', 'card'],                        divisionHints: ['cards'] },
    { keywords: ['lending', 'loans', 'loan', 'mortgage'], divisionHints: ['lending', 'loans'] },
    { keywords: ['technology', 'tech', 'it'],             divisionHints: ['technology'] },
    { keywords: ['risk', 'compliance', 'regulatory'],     divisionHints: ['risk'] },
    { keywords: ['operations', 'ops'],                    divisionHints: ['operations'] },
    { keywords: ['finance', 'treasury'],                  divisionHints: ['finance'] },
    { keywords: ['hr', 'human resources', 'people'],      divisionHints: ['human resources', 'hr'] },
    { keywords: ['marketing', 'digital'],                 divisionHints: ['marketing'] },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

function slugify(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function fuzzyDivisionMatch(
    domain: string,
    divisionNameToId: Map<string, string>,
): string | undefined {
    const domainLower = domain.toLowerCase();

    for (const [divName, divId] of divisionNameToId) {
        if (domainLower.includes(divName) || divName.includes(domainLower)) {
            return divId;
        }
    }

    for (const alias of DOMAIN_ALIAS_TABLE) {
        const domainMatches = alias.keywords.some(kw => domainLower.includes(kw));
        if (domainMatches) {
            for (const hint of alias.divisionHints) {
                for (const [divName, divId] of divisionNameToId) {
                    if (divName.includes(hint)) {
                        return divId;
                    }
                }
            }
        }
    }

    return undefined;
}

/**
 * Convert Candid metadata tuples Array<[string, string]> to Record<string, string>.
 */
function tuplesToRecord(tuples: Array<[string, string]> | undefined): Record<string, string> {
    if (!tuples || tuples.length === 0) return {};
    return Object.fromEntries(tuples);
}

/**
 * Filter entities by import session ID stored in metadata.
 */
function matchesSession(
    metadata: Array<[string, string]> | Record<string, string> | undefined,
    sessionId: string,
): boolean {
    if (!metadata) return false;
    if (Array.isArray(metadata)) {
        return metadata.some(([k, v]) => k === '_import_session_id' && v === sessionId);
    }
    return metadata['_import_session_id'] === sessionId;
}

// ---------------------------------------------------------------------------
// ServiceMapDataBuilder
// ---------------------------------------------------------------------------

export class ServiceMapDataBuilder {
    constructor(private canisterClient: CanisterClientInterface) {}

    /**
     * Fetches all 15 EA entity types from the canister and assembles
     * a ServiceMapData payload with ring assignments for the concentric
     * ring visualizer.
     */
    async build(sessionId?: string): Promise<ServiceMapData> {
        // 1. Fetch all 15 entity types in parallel
        const [
            organizations,
            applications,
            technicalServices,
            businessServices,
            dataObjects,
            hrRoles,
            relationships,
            risks,
            controls,
            capabilities,
            processes,
            processConnectors,
            compliance,
            integrations,
        ] = await Promise.all([
            this.canisterClient.listEAOrganizations(),
            this.canisterClient.listEAApplications(),
            this.canisterClient.listEATechnicalServices(),
            this.canisterClient.listEABusinessServices(),
            this.canisterClient.listEADataObjects(),
            this.canisterClient.listEAHRRoles(),
            this.canisterClient.listEARelationships(),
            this.fetchRisks(),
            this.fetchControls(),
            this.canisterClient.listEACapabilities(),
            this.canisterClient.listEAProcesses(),
            this.canisterClient.listEAProcessConnectors(),
            this.canisterClient.listEACompliance(),
            this.canisterClient.listEAIntegrations(),
        ]);

        // 2. Apply session filter if provided
        const f = <T>(items: T[], metaKey: keyof T): T[] =>
            sessionId ? items.filter(item => matchesSession(item[metaKey] as any, sessionId)) : items;

        const filtOrgs = f(organizations, 'metadata' as keyof EAOrganization);
        const filtApps = f(applications, 'metadata' as keyof EAApplicationEntity);
        const filtTechSvcs = f(technicalServices, 'metadata' as keyof EATechnicalService);
        const filtBizSvcs = f(businessServices, 'metadata' as keyof EABusinessServiceEntity);
        const filtDataObjs = f(dataObjects, 'metadata' as keyof EADataObject);
        const filtHRRoles = f(hrRoles, 'metadata' as keyof EAHRRole);
        const filtRels = f(relationships, 'metadata' as keyof EARelationship);
        const filtRisks = sessionId
            ? risks.filter(r => matchesSession(r.metadata, sessionId))
            : risks;
        const filtControls = sessionId
            ? controls.filter(c => matchesSession(c.metadata, sessionId))
            : controls;
        const filtCapabilities = f(capabilities, 'metadata' as keyof EACapability);
        const filtProcesses = f(processes, 'metadata' as keyof EAProcess);
        const filtProcConns = f(processConnectors, 'metadata' as keyof EAProcessConnector);
        const filtCompliance = f(compliance, 'metadata' as keyof EACompliance);
        const filtIntegrations = f(integrations, 'metadata' as keyof EAIntegration);

        const nodes: ServiceMapNode[] = [];
        const edges: ServiceMapEdge[] = [];

        // 3. Ring 0 — Organization root node
        this.buildRootNode(filtOrgs, nodes);

        // 4. Ring 1 — Division compound nodes
        const { divisionNameToId, departmentToDivisionId } =
            this.buildDivisionNodes(filtOrgs, nodes);

        // 5. Ring 2 — Business Services + Capabilities
        this.buildBusinessServiceNodes(filtBizSvcs, divisionNameToId, nodes);
        this.buildCapabilityNodes(filtCapabilities, divisionNameToId, nodes);
        this.buildProcessNodes(filtProcesses, divisionNameToId, nodes);

        // 6. Ring 3 — Applications + Technical Services
        const applicationAffinityMap = this.buildApplicationNodes(filtApps, divisionNameToId, nodes);
        this.buildTechnicalServiceNodes(filtTechSvcs, applicationAffinityMap, nodes);

        // 7. Ring 4 — Data Objects + HR Roles
        this.buildDataObjectNodes(filtDataObjs, filtApps, applicationAffinityMap, nodes);
        this.buildHRRoleNodes(filtHRRoles, departmentToDivisionId, divisionNameToId, nodes);

        // 8. Ring 5 — Governance: Risks, Controls, Compliance
        this.buildRiskNodes(filtRisks, divisionNameToId, nodes);
        this.buildControlNodes(filtControls, nodes);
        this.buildComplianceNodes(filtCompliance, divisionNameToId, nodes);

        // 9. Build edges
        const nodeIdSet = new Set(nodes.map(n => n.id));

        // 9a. EA Relationship edges
        this.buildRelationshipEdges(filtRels, nodeIdSet, edges);

        // 9b. Integration edges
        this.buildIntegrationEdges(filtIntegrations, filtApps, nodeIdSet, edges);

        // 9c. Risk-Control edges
        this.buildRiskControlEdges(filtRisks, filtControls, nodeIdSet, edges);

        // 9d. Compliance-App edges
        this.buildComplianceEdges(filtCompliance, filtApps, nodeIdSet, edges);

        // 9e. Process Connector edges
        this.buildProcessConnectorEdges(filtProcConns, nodeIdSet, edges);

        // 9f. Infrastructure → Application edges (from "Supports Applications" column)
        this.buildInfraAppEdges(nodes, nodeIdSet, edges);

        // 10. Update division compound node child counts
        this.updateCompoundChildCounts(nodes);

        // 11. Statistics
        const statistics = this.computeStatistics(nodes, filtOrgs, filtApps, filtCapabilities,
            filtProcesses, filtIntegrations, filtCompliance, filtRisks, filtControls);

        return { nodes, edges, statistics, sessionId };
    }

    // -----------------------------------------------------------------------
    // Risk / Control fetching (generic list endpoints)
    // -----------------------------------------------------------------------

    private async fetchRisks(): Promise<RiskEntity[]> {
        try {
            return await this.canisterClient.callCanister<RiskEntity[]>(
                'vector_db', 'list_risks', []
            ) ?? [];
        } catch {
            return [];
        }
    }

    private async fetchControls(): Promise<ControlEntity[]> {
        try {
            return await this.canisterClient.callCanister<ControlEntity[]>(
                'vector_db', 'list_controls', []
            ) ?? [];
        } catch {
            return [];
        }
    }

    // -----------------------------------------------------------------------
    // Ring 0 — Organization Root
    // -----------------------------------------------------------------------

    private buildRootNode(organizations: EAOrganization[], nodes: ServiceMapNode[]): void {
        const totalHeadcount = organizations.reduce((s, o) => s + o.headcount, 0);
        const divisionCount = new Set(organizations.map(o => o.division)).size;

        nodes.push({
            id: 'org-root',
            label: 'Enterprise',
            entityType: 'organization-root',
            ring: Ring.CENTER,
            ringGroup: 'center',
            isCompound: true,
            metadata: {
                headcount: String(totalHeadcount),
                divisions: String(divisionCount),
            },
        });
    }

    // -----------------------------------------------------------------------
    // Ring 1 — Divisions (compound nodes)
    // -----------------------------------------------------------------------

    private buildDivisionNodes(
        organizations: EAOrganization[],
        nodes: ServiceMapNode[],
    ): {
        divisionNameToId: Map<string, string>;
        departmentToDivisionId: Map<string, string>;
    } {
        const divisionMap = new Map<string, EAOrganization[]>();
        for (const org of organizations) {
            const existing = divisionMap.get(org.division) ?? [];
            existing.push(org);
            divisionMap.set(org.division, existing);
        }

        const divisionNameToId = new Map<string, string>();
        const departmentToDivisionId = new Map<string, string>();

        for (const [division, orgs] of divisionMap) {
            const divId = slugify(division);
            divisionNameToId.set(division.toLowerCase(), divId);

            const totalHeadcount = orgs.reduce((s, o) => s + o.headcount, 0);
            const uniqueDepartments = new Set(orgs.map(o => o.department));

            for (const org of orgs) {
                departmentToDivisionId.set(org.department.toLowerCase(), divId);
            }

            nodes.push({
                id: divId,
                label: division,
                entityType: 'division',
                ring: Ring.DIVISION,
                ringGroup: 'divisions',
                isCompound: true,
                parentId: 'org-root',
                metadata: {
                    headcount: String(totalHeadcount),
                    departments: String(uniqueDepartments.size),
                },
            });
        }

        return { divisionNameToId, departmentToDivisionId };
    }

    // -----------------------------------------------------------------------
    // Ring 2 — Business Services
    // -----------------------------------------------------------------------

    private buildBusinessServiceNodes(
        businessServices: EABusinessServiceEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const bs of businessServices) {
            const affinity = fuzzyDivisionMatch(bs.service_domain, divisionNameToId);

            nodes.push({
                id: bs.service_id,
                label: bs.service_offering || bs.business_service,
                entityType: 'business-service',
                ring: Ring.SERVICES_CAPABILITIES,
                ringGroup: 'services',
                clusterAffinity: affinity,
                metadata: {
                    service_domain: bs.service_domain,
                    business_service: bs.business_service,
                    channel: bs.channel,
                    sla: bs.sla,
                    service_owner: bs.service_owner,
                    technical_service: bs.technical_service,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 2 — Capabilities
    // -----------------------------------------------------------------------

    private buildCapabilityNodes(
        capabilities: EACapability[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const cap of capabilities) {
            const affinity = fuzzyDivisionMatch(cap.domain, divisionNameToId);

            nodes.push({
                id: cap.capability_id,
                label: cap.capability_l2 || cap.capability_l1,
                entityType: 'capability',
                ring: Ring.SERVICES_CAPABILITIES,
                ringGroup: 'capabilities',
                clusterAffinity: affinity,
                metadata: {
                    domain: cap.domain,
                    capability_l1: cap.capability_l1,
                    capability_l2: cap.capability_l2,
                    description: cap.description,
                    owner: cap.owner,
                    key_systems: cap.key_systems,
                    maturity_level: cap.maturity_level,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 2 — Processes
    // -----------------------------------------------------------------------

    private buildProcessNodes(
        processes: EAProcess[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const proc of processes) {
            const affinity = fuzzyDivisionMatch(proc.process_domain, divisionNameToId);

            nodes.push({
                id: proc.process_id,
                label: proc.process_name,
                entityType: 'process',
                ring: Ring.SERVICES_CAPABILITIES,
                ringGroup: 'processes',
                clusterAffinity: affinity,
                metadata: {
                    process_domain: proc.process_domain,
                    trigger: proc.trigger,
                    steps: proc.steps,
                    owner: proc.owner,
                    sla: proc.sla,
                    volume_per_day: String(proc.volume_per_day),
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 3 — Applications
    // -----------------------------------------------------------------------

    private buildApplicationNodes(
        applications: EAApplicationEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): Map<string, string | undefined> {
        const affinityMap = new Map<string, string | undefined>();

        for (const app of applications) {
            const affinity = fuzzyDivisionMatch(app.domain, divisionNameToId);
            affinityMap.set(app.app_id, affinity);

            nodes.push({
                id: app.app_id,
                label: app.name,
                entityType: 'application',
                ring: Ring.APPLICATIONS,
                ringGroup: 'applications',
                clusterAffinity: affinity,
                metadata: {
                    vendor: app.vendor,
                    app_type: app.app_type,
                    domain: app.domain,
                    criticality: app.criticality,
                    users: String(app.users),
                    integration_method: app.integration_method,
                },
            });
        }

        return affinityMap;
    }

    // -----------------------------------------------------------------------
    // Ring 3 — Technical Services
    // -----------------------------------------------------------------------

    private buildTechnicalServiceNodes(
        technicalServices: EATechnicalService[],
        applicationAffinityMap: Map<string, string | undefined>,
        nodes: ServiceMapNode[],
    ): void {
        for (const ts of technicalServices) {
            const affinity = applicationAffinityMap.get(ts.application);
            const isInfra = ts.service_type.startsWith('infrastructure:');

            // Extract infra metadata if available
            const metaMap = new Map<string, string>();
            if (ts.metadata) {
                for (const [k, v] of ts.metadata) {
                    metaMap.set(k, v);
                }
            }

            nodes.push({
                id: ts.service_id,
                label: ts.name,
                entityType: isInfra ? 'infrastructure' : 'technical-service',
                ring: isInfra ? Ring.INFRASTRUCTURE : Ring.APPLICATIONS,
                ringGroup: isInfra ? 'infrastructure' : 'technical-services',
                clusterAffinity: affinity,
                metadata: isInfra ? {
                    device_type: metaMap.get('device_type') || ts.service_type.replace('infrastructure:', ''),
                    environment: metaMap.get('environment') || '',
                    location: metaMap.get('location') || '',
                    specification: metaMap.get('specification') || '',
                    criticality: metaMap.get('criticality') || ts.availability_sla,
                    supports_applications: ts.application,
                    owner: ts.owner,
                } : {
                    service_type: ts.service_type,
                    api_interface: ts.api_interface,
                    availability_sla: ts.availability_sla,
                    owner: ts.owner,
                    application: ts.application,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 4 — Data Objects
    // -----------------------------------------------------------------------

    private buildDataObjectNodes(
        dataObjects: EADataObject[],
        applications: EAApplicationEntity[],
        applicationAffinityMap: Map<string, string | undefined>,
        nodes: ServiceMapNode[],
    ): void {
        const appNameToId = new Map<string, string>();
        for (const app of applications) {
            appNameToId.set(app.name.toLowerCase(), app.app_id);
        }

        for (const dobj of dataObjects) {
            let affinity: string | undefined;
            const matchedAppId = appNameToId.get(dobj.source_system.toLowerCase());
            if (matchedAppId !== undefined) {
                affinity = applicationAffinityMap.get(matchedAppId);
            }

            nodes.push({
                id: dobj.data_object_id,
                label: dobj.name,
                entityType: 'data',
                ring: Ring.INFRASTRUCTURE,
                ringGroup: 'data-objects',
                clusterAffinity: affinity,
                metadata: {
                    domain: dobj.domain,
                    classification: dobj.classification,
                    source_system: dobj.source_system,
                    owner: dobj.owner,
                    retention: dobj.retention,
                    key_attributes: dobj.key_attributes,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 4 — HR Roles
    // -----------------------------------------------------------------------

    private buildHRRoleNodes(
        hrRoles: EAHRRole[],
        departmentToDivisionId: Map<string, string>,
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const hr of hrRoles) {
            const affinity =
                divisionNameToId.get(hr.division.toLowerCase()) ??
                departmentToDivisionId.get(hr.department.toLowerCase());

            nodes.push({
                id: hr.role_id,
                label: hr.role,
                entityType: 'hr-role',
                ring: Ring.INFRASTRUCTURE,
                ringGroup: 'hr-roles',
                clusterAffinity: affinity,
                metadata: {
                    division: hr.division,
                    department: hr.department,
                    grade: hr.grade,
                    headcount: String(hr.headcount),
                    location: hr.location,
                    key_skills: hr.key_skills,
                    systems_used: hr.systems_used,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 5 — Risks
    // -----------------------------------------------------------------------

    private buildRiskNodes(
        risks: RiskEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const risk of risks) {
            const affinity = fuzzyDivisionMatch(risk.category, divisionNameToId);

            nodes.push({
                id: risk.risk_id,
                label: risk.name,
                entityType: 'risk',
                ring: Ring.GOVERNANCE,
                ringGroup: 'risks',
                clusterAffinity: affinity,
                metadata: {
                    category: risk.category,
                    severity: risk.severity,
                    likelihood: risk.likelihood || '',
                    owner: risk.owner || '',
                    status: risk.status || '',
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 5 — Controls
    // -----------------------------------------------------------------------

    private buildControlNodes(
        controls: ControlEntity[],
        nodes: ServiceMapNode[],
    ): void {
        for (const ctrl of controls) {
            nodes.push({
                id: ctrl.control_id,
                label: ctrl.name,
                entityType: 'control',
                ring: Ring.GOVERNANCE,
                ringGroup: 'controls',
                metadata: {
                    control_type: ctrl.control_type,
                    effectiveness: ctrl.effectiveness,
                    owner: ctrl.owner || '',
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Ring 5 — Compliance
    // -----------------------------------------------------------------------

    private buildComplianceNodes(
        compliance: EACompliance[],
        divisionNameToId: Map<string, string>,
        nodes: ServiceMapNode[],
    ): void {
        for (const comp of compliance) {
            const affinity = fuzzyDivisionMatch(comp.category, divisionNameToId);

            nodes.push({
                id: comp.obligation_id,
                label: comp.regulation,
                entityType: 'compliance',
                ring: Ring.GOVERNANCE,
                ringGroup: 'compliance',
                clusterAffinity: affinity,
                metadata: {
                    regulator: comp.regulator,
                    category: comp.category,
                    status: comp.status,
                    executive: comp.executive,
                    in_scope_apps: (comp.in_scope_apps || []).join(', '),
                    linked_controls: (comp.linked_controls || []).join(', '),
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Edges: EA Relationships
    // -----------------------------------------------------------------------

    private buildRelationshipEdges(
        relationships: EARelationship[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        for (const rel of relationships) {
            if (!nodeIdSet.has(rel.source_id) || !nodeIdSet.has(rel.target_id)) continue;

            const weight = RELATIONSHIP_WEIGHT_MAP[rel.relationship_type] ?? DEFAULT_RELATIONSHIP_WEIGHT;

            edges.push({
                id: rel.relationship_id,
                source: rel.source_id,
                target: rel.target_id,
                relationshipType: rel.relationship_type,
                weight,
                edgeCategory: 'relationship',
                style: EDGE_STYLES['relationship'],
            });
        }
    }

    // -----------------------------------------------------------------------
    // Edges: Integrations (app-to-app, blue dashed)
    // -----------------------------------------------------------------------

    private buildIntegrationEdges(
        integrations: EAIntegration[],
        applications: EAApplicationEntity[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        // Build a lookup from system name (lowercase) → app_id
        const systemToAppId = new Map<string, string>();
        for (const app of applications) {
            systemToAppId.set(app.name.toLowerCase(), app.app_id);
        }

        for (const integ of integrations) {
            const sourceId = systemToAppId.get(integ.source_system.toLowerCase());
            const targetId = systemToAppId.get(integ.target_system.toLowerCase());

            if (!sourceId || !targetId) continue;
            if (!nodeIdSet.has(sourceId) || !nodeIdSet.has(targetId)) continue;

            edges.push({
                id: `integ-${integ.integration_id}`,
                source: sourceId,
                target: targetId,
                relationshipType: integ.integration_type,
                weight: 4,
                edgeCategory: 'integration',
                style: EDGE_STYLES['integration'],
            });
        }
    }

    // -----------------------------------------------------------------------
    // Edges: Risk ↔ Control (green solid)
    // -----------------------------------------------------------------------

    private buildRiskControlEdges(
        risks: RiskEntity[],
        controls: ControlEntity[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        let counter = 0;
        const seenPairs = new Set<string>();

        // Risk → Control from linked_controls
        for (const risk of risks) {
            if (!nodeIdSet.has(risk.risk_id)) continue;
            for (const controlId of (risk.linked_controls || [])) {
                if (!nodeIdSet.has(controlId)) continue;
                const pairKey = `${risk.risk_id}:${controlId}`;
                if (seenPairs.has(pairKey)) continue;
                seenPairs.add(pairKey);

                edges.push({
                    id: `rc-${counter++}`,
                    source: risk.risk_id,
                    target: controlId,
                    relationshipType: 'mitigated_by',
                    weight: 5,
                    edgeCategory: 'control-risk',
                    style: EDGE_STYLES['control-risk'],
                });
            }

            // Risk → Application from linked_applications (orange solid)
            for (const appId of (risk.linked_applications || [])) {
                if (!nodeIdSet.has(appId)) continue;
                edges.push({
                    id: `ra-${counter++}`,
                    source: risk.risk_id,
                    target: appId,
                    relationshipType: 'affects',
                    weight: 3,
                    edgeCategory: 'risk-app',
                    style: EDGE_STYLES['risk-app'],
                });
            }
        }

        // Control → Risk from linked_risks (deduplicated)
        for (const ctrl of controls) {
            if (!nodeIdSet.has(ctrl.control_id)) continue;
            for (const riskId of (ctrl.linked_risks || [])) {
                if (!nodeIdSet.has(riskId)) continue;
                const pairKey = `${riskId}:${ctrl.control_id}`;
                if (seenPairs.has(pairKey)) continue;
                seenPairs.add(pairKey);

                edges.push({
                    id: `cr-${counter++}`,
                    source: ctrl.control_id,
                    target: riskId,
                    relationshipType: 'mitigates',
                    weight: 5,
                    edgeCategory: 'control-risk',
                    style: EDGE_STYLES['control-risk'],
                });
            }
        }
    }

    // -----------------------------------------------------------------------
    // Edges: Compliance → Application (teal dotted)
    // -----------------------------------------------------------------------

    private buildComplianceEdges(
        compliance: EACompliance[],
        applications: EAApplicationEntity[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        // Build name→ID lookup (in_scope_apps contains names, not IDs)
        const appNameToId = new Map<string, string>();
        for (const app of applications) {
            appNameToId.set(app.name.toLowerCase().trim(), app.app_id);
        }

        let counter = 0;

        for (const comp of compliance) {
            if (!nodeIdSet.has(comp.obligation_id)) continue;

            for (const appRef of (comp.in_scope_apps || [])) {
                // Resolve: try as name first, then as direct ID
                const resolvedId = appNameToId.get(appRef.toLowerCase().trim()) ?? appRef;
                if (!nodeIdSet.has(resolvedId)) continue;

                edges.push({
                    id: `comp-app-${counter++}`,
                    source: comp.obligation_id,
                    target: resolvedId,
                    relationshipType: 'regulates',
                    weight: 3,
                    edgeCategory: 'compliance-app',
                    style: EDGE_STYLES['compliance-app'],
                });
            }
        }
    }

    // -----------------------------------------------------------------------
    // Edges: Process Connectors (purple dashed, step sequencing)
    // -----------------------------------------------------------------------

    private buildProcessConnectorEdges(
        connectors: EAProcessConnector[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        // Group connectors by process_id, sorted by step_number
        const byProcess = new Map<string, EAProcessConnector[]>();
        for (const conn of connectors) {
            const existing = byProcess.get(conn.process_id) ?? [];
            existing.push(conn);
            byProcess.set(conn.process_id, existing);
        }

        let counter = 0;

        for (const [processId, steps] of byProcess) {
            if (!nodeIdSet.has(processId)) continue;

            // Sort by step_number for sequential edges
            steps.sort((a, b) => a.step_number - b.step_number);

            // Connect process → first step's linked app
            for (const step of steps) {
                if (step.linked_app_id && nodeIdSet.has(step.linked_app_id)) {
                    edges.push({
                        id: `pconn-${counter++}`,
                        source: processId,
                        target: step.linked_app_id,
                        relationshipType: `step-${step.step_number}`,
                        weight: 2,
                        edgeCategory: 'process-connector',
                        style: EDGE_STYLES['process-connector'],
                    });
                }
            }
        }
    }

    // -----------------------------------------------------------------------
    // Edges: Infrastructure → Application (from "Supports Applications")
    // -----------------------------------------------------------------------

    private buildInfraAppEdges(
        nodes: ServiceMapNode[],
        nodeIdSet: Set<string>,
        edges: ServiceMapEdge[],
    ): void {
        // Build a lookup from app name (lowercase) → node id
        const appNameToId = new Map<string, string>();
        for (const n of nodes) {
            if (n.entityType === 'application') {
                appNameToId.set(n.label.toLowerCase().trim(), n.id);
            }
        }

        let counter = 0;
        for (const n of nodes) {
            if (n.entityType !== 'infrastructure') continue;
            const supportsStr = n.metadata['supports_applications'] as string;
            if (!supportsStr) continue;

            // Split on commas
            const appRefs = supportsStr.split(',').map(s => s.trim()).filter(Boolean);
            for (const appRef of appRefs) {
                // Resolve: try name lookup first, then direct ID lookup
                const resolvedId = appNameToId.get(appRef.toLowerCase()) ?? appRef;
                if (!nodeIdSet.has(resolvedId)) continue;

                edges.push({
                    id: `infra-app-${counter++}`,
                    source: n.id,
                    target: resolvedId,
                    relationshipType: 'supports',
                    weight: 3,
                    edgeCategory: 'infrastructure',
                    style: {
                        lineColor: '#10b981',
                        lineStyle: 'solid' as const,
                        width: 1.5,
                        opacity: 0.6,
                    },
                });
            }
        }
        console.log(`[ServiceMap] Infrastructure→App edges: ${counter}`);
    }

    // -----------------------------------------------------------------------
    // Compound node child counts
    // -----------------------------------------------------------------------

    private updateCompoundChildCounts(nodes: ServiceMapNode[]): void {
        const childCounts = new Map<string, number>();

        for (const node of nodes) {
            if (node.clusterAffinity) {
                const count = childCounts.get(node.clusterAffinity) ?? 0;
                childCounts.set(node.clusterAffinity, count + 1);
            }
        }

        for (const node of nodes) {
            if (node.isCompound && childCounts.has(node.id)) {
                node.childCount = childCounts.get(node.id)!;
            }
        }
    }

    // -----------------------------------------------------------------------
    // Statistics
    // -----------------------------------------------------------------------

    private computeStatistics(
        nodes: ServiceMapNode[],
        organizations: EAOrganization[],
        applications: EAApplicationEntity[],
        capabilities: EACapability[],
        processes: EAProcess[],
        integrations: EAIntegration[],
        compliance: EACompliance[],
        risks: RiskEntity[],
        controls: ControlEntity[],
    ): ServiceMapStatistics {
        const byRing: Record<number, number> = {};
        const byType: Record<string, number> = {};

        for (const node of nodes) {
            byRing[node.ring] = (byRing[node.ring] ?? 0) + 1;
            byType[node.entityType] = (byType[node.entityType] ?? 0) + 1;
        }

        const divisionCount = new Set(organizations.map(o => o.division)).size;
        const totalHeadcount = organizations.reduce((s, o) => s + o.headcount, 0);

        return {
            totalEntities: nodes.length,
            byRing,
            byType,
            divisions: divisionCount,
            totalHeadcount,
            totalApplications: applications.length,
            totalCapabilities: capabilities.length,
            totalProcesses: processes.length,
            totalIntegrations: integrations.length,
            totalCompliance: compliance.length,
            totalRisks: risks.length,
            totalControls: controls.length,
        };
    }
}
